"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";
import CategoryBadge from "@/components/blog/CategoryBadge";
import { articlePath, formatDateLabel, type BlogCardData } from "@/lib/blog";

/** Artikel-Karte im Blog-Grid. */
export default function BlogCard({
  article,
  index = 0,
}: {
  article: BlogCardData;
  index?: number;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.07 }}
      className="group h-full"
    >
      <Link
        href={articlePath(article)}
        className="card-flat flex flex-col h-full bg-white/60 p-4 transition-all duration-300 hover:-translate-y-1"
      >
        <span className="relative block rounded-xl overflow-hidden aspect-[16/9] mb-4">
          <Image
            src={article.image}
            alt={article.imageAlt}
            width={750}
            height={874}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <CategoryBadge
            slug={article.category}
            name={article.categoryName}
            className="absolute top-3 left-3"
          />
        </span>

        <span className="flex flex-col gap-3 px-1.5 pb-1.5 flex-1">
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-faint">
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays size={13} className="text-accent" aria-hidden="true" />
              <time dateTime={article.publishedAt}>{formatDateLabel(article.publishedAt)}</time>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} className="text-accent" aria-hidden="true" />
              {article.readingMinutes} Min. Lesezeit
            </span>
          </span>

          <span className="font-display font-bold text-lg leading-snug text-ink group-hover:text-accent transition-colors">
            {article.title}
          </span>

          <span className="text-sm text-ink-soft leading-relaxed line-clamp-3">
            {article.excerpt}
          </span>

          <span className="mt-auto inline-flex items-center gap-1.5 pt-2 text-sm font-semibold text-accent">
            Weiterlesen
            <ArrowRight
              size={14}
              className="group-hover:translate-x-0.5 transition-transform"
              aria-hidden="true"
            />
          </span>
        </span>
      </Link>
    </motion.article>
  );
}
